import { cva } from "class-variance-authority";

// Ardoise design-system button -- see Figma "Ardoise / Button".
// Composed where the styling is axis-independent (size, and renderMode structure);
// colors are coupled to role x renderMode, so only those live in compoundVariants.
const ardoiseButtonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-sm transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-neutral-300 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:shrink-0",
  {
    variants: {
      // kind has no standalone styling -- it only selects the color pairing in compoundVariants below.
      // (named kind to avoid clashing with the HTML role / type attributes on Button)
      kind: {
        primary: "",
        accent: "",
        danger: "",
        success: "",
      },
      // All renderModes carry a border so switching between them never shifts layout;
      // fill/ghost use a transparent one (the background shows through it).
      renderMode: {
        fill: "border border-transparent",
        outline: "border bg-neutral-0",
        ghost: "border border-transparent bg-transparent",
      },
      // Sizing depends on typography, see compoundVariants.
      size: {
        xs: "",
        s: "",
        m: "",
        l: "",
      },
      iconOnly: {
        true: "",
        false: "",
      },
      // Remove v1 once the `typography-v2-buttons` front flag is rolled out.
      typography: {
        // oxlint-disable-next-line grafit-oxlint/no-tailwind-font-classes -- legacy v1 behind a feature flag
        v1: "font-medium leading-none",
        v2: "[&_svg]:size-[1em]",
      },
    },
    compoundVariants: [
      // oxlint-disable-next-line grafit-oxlint/no-tailwind-font-classes -- legacy v1 behind a feature flag
      { typography: "v1", size: "xs", class: "text-xs [&_svg]:size-3" },
      // oxlint-disable-next-line grafit-oxlint/no-tailwind-font-classes -- legacy v1 behind a feature flag
      { typography: "v1", size: "s", class: "text-[13px] [&_svg]:size-3.5" },
      // oxlint-disable-next-line grafit-oxlint/no-tailwind-font-classes -- legacy v1 behind a feature flag
      { typography: "v1", size: ["m", "l"], class: "text-sm [&_svg]:size-4" },
      { typography: "v2", size: "xs", class: "typo-body-xs-medium" },
      { typography: "v2", size: "s", class: "typo-body-sm-medium" },
      { typography: "v2", size: ["m", "l"], class: "typo-body-base-medium" },
      // - icon-only mode: symmetric p (= label-mode py) and gap-0 so multiple icons sit flush
      { typography: "v1", iconOnly: false, size: "xs", class: "gap-1 px-1.5 py-1" },
      { typography: "v1", iconOnly: false, size: "s", class: "gap-1 px-2 py-1.5" },
      { typography: "v1", iconOnly: false, size: "m", class: "gap-1.5 px-2 py-1.5" },
      { typography: "v1", iconOnly: false, size: "l", class: "gap-2 px-2.5 py-2" },
      { typography: "v1", iconOnly: true, size: "xs", class: "gap-0 p-1" },
      { typography: "v1", iconOnly: true, size: ["s", "m"], class: "gap-0 p-1.5" },
      { typography: "v1", iconOnly: true, size: "l", class: "gap-0 p-2" },
      // Scales with the text. Padding is 1px under Figma's: its stroke is inside, our border adds to the box.
      { typography: "v2", iconOnly: false, size: "xs", class: "gap-typo-4 px-typo-5 py-typo-3" },
      { typography: "v2", iconOnly: false, size: "s", class: "gap-typo-4 px-typo-7 py-typo-5" },
      { typography: "v2", iconOnly: false, size: "m", class: "gap-typo-6 px-typo-7 py-typo-5" },
      { typography: "v2", iconOnly: false, size: "l", class: "gap-typo-8 px-typo-9 py-typo-7" },
      { typography: "v2", iconOnly: true, size: "xs", class: "gap-0 p-typo-3" },
      { typography: "v2", iconOnly: true, size: ["s", "m"], class: "gap-0 p-typo-5" },
      { typography: "v2", iconOnly: true, size: "l", class: "gap-0 p-typo-7" },
      // Fill
      {
        kind: "primary",
        renderMode: "fill",
        class: "bg-action-400 text-font-primary hover:bg-action-300 active:bg-action-500 disabled:text-font-disabled",
      },
      {
        kind: "accent",
        renderMode: "fill",
        class: "bg-accent-700 text-neutral-0 hover:bg-accent-600 active:bg-accent-800",
      },
      {
        kind: "danger",
        renderMode: "fill",
        class: "bg-danger-500 text-neutral-0 hover:bg-danger-400 active:bg-danger-600",
      },
      {
        kind: "success",
        renderMode: "fill",
        class: "bg-success-500 text-neutral-0 hover:bg-success-400 active:bg-success-600",
      },
      // Outline
      {
        kind: "primary",
        renderMode: "outline",
        class:
          "border-neutral-300 text-font-primary hover:bg-neutral-100/80 active:bg-neutral-200 disabled:bg-neutral-200",
      },
      {
        kind: "accent",
        renderMode: "outline",
        class:
          "border-accent-600 text-font-primary hover:bg-accent-100/80 active:border-accent-500 active:bg-accent-400 disabled:border-accent-500 disabled:bg-accent-300",
      },
      {
        kind: "danger",
        renderMode: "outline",
        class:
          "border-danger-300 text-font-danger hover:bg-danger-50 active:bg-danger-200 disabled:border-neutral-300 disabled:bg-neutral-200 disabled:text-font-primary",
      },
      {
        kind: "success",
        renderMode: "outline",
        class:
          "border-success-300 text-font-success hover:bg-success-50 active:bg-success-200 disabled:border-neutral-300 disabled:bg-neutral-200 disabled:text-font-primary",
      },
      // Ghost
      {
        kind: "primary",
        renderMode: "ghost",
        class: "text-font-primary hover:bg-neutral-200 active:bg-neutral-200 disabled:bg-neutral-200",
      },
      {
        kind: "accent",
        renderMode: "ghost",
        class: "text-font-primary hover:bg-accent-100 active:bg-accent-100 disabled:bg-accent-300",
      },
      {
        kind: "danger",
        renderMode: "ghost",
        class:
          "text-font-danger hover:bg-danger-100 active:bg-danger-100 disabled:bg-neutral-200 disabled:text-font-primary",
      },
      {
        kind: "success",
        renderMode: "ghost",
        class:
          "text-font-success hover:bg-success-100 active:bg-success-100 disabled:bg-neutral-200 disabled:text-font-primary",
      },
    ],
    defaultVariants: {
      kind: "primary",
      renderMode: "fill",
      size: "m",
      iconOnly: false,
      typography: "v2",
    },
  },
);

export { ardoiseButtonVariants };
